import type { FC, ReactNode } from "react";
import { useEffect } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { TimeIcon } from "@jti/brand-icons/time";
import { Badge } from "@jti/core-components/components/Badge";
import { Box } from "@jti/core-components/components/Box";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { interpolateCopy } from "@jti/fe-common/utils";
import { formatPrice } from "@jti/format-price";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { useLoyalty } from "@jti/ui/hooks/useLoyalty";
import { useLoyaltyRestrictedContent } from "@jti/ui/hooks/useLoyaltyRestrictedContent";
import LoyaltyLogo from "@jti/ui/icons/loyalty_logo.svg";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { ProductLoyaltyBlockProps } from "./ProductLoyaltyBlock.types";
import setLimitedRewardTimerContent from "./setLimitedRewardTimerContent";
import { useLoyaltyProductData } from "./useLoyaltyProductData";

import "./style.scss";
import styles from "./ProductLoyaltyBlock.module.scss";

const { sanitize } = dompurify;

type RewardTextProps = {
  children: ReactNode;
};

const RewardText: FC<RewardTextProps> = ({ children }) => {
  return <span className="productDetails__stockText loyalty-reward-block__reward">{children}</span>;
};

export const ProductLoyaltyBlock: FC<ProductLoyaltyBlockProps> = ({
  knowMoreUrl,
  sku,
  selectedQuantity,
  unitPrice,
  currency,
  loyaltyRewardCopy,
  isLoyaltyRewardCopyAuthoringEnabled = false,
  onPriceTagStateChange,
}) => {
  const { __: translate } = useTranslations();
  const { LOYALTY_KNOW_MORE_URL } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const isLoyaltyActive = useLoyalty();
  const { isRestricted: isLoyaltyRestricted } = useLoyaltyRestrictedContent();
  const isRewardCopyAuthoringEnabled = isLoyaltyRewardCopyAuthoringEnabled;
  const { isLoaded, points, timeLimited, loyaltyDiscountState, highestTier, priceTagState } = useLoyaltyProductData({
    sku,
    selectedQuantity,
    unitPrice,
    isLoyaltyActive,
    isLoyaltyRestricted,
    isRewardCopyAuthoringEnabled,
  });
  const isTimeLimited = timeLimited > 0;
  const className = classNames("loyalty-reward-block", {
    "loyalty-reward-block--limitedRewardHolder": isTimeLimited,
  });

  const hours = translate("product.info.hours");
  const minutes = translate("product.info.minutes");
  const dayssingular = translate("product.info.daysSingular");
  const weekssingular = translate("product.info.weeksSingular");
  const daysplural = translate("product.info.daysPlural");
  const weeksplural = translate("product.info.weeksPlural");

  const formatDiscountAmount = (value: number) => {
    if (!currency) {
      return String(value);
    }

    return formatPrice({ currency, value });
  };

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    onPriceTagStateChange?.(priceTagState);
  }, [isLoaded, onPriceTagStateChange, priceTagState]);

  const shouldHideUntilLoaded = !isRewardCopyAuthoringEnabled && !isLoaded;
  const shouldHideForZeroPoints = isLoaded && points <= 0 && !isRewardCopyAuthoringEnabled;

  if (shouldHideUntilLoaded || shouldHideForZeroPoints || isLoyaltyRestricted) {
    return null;
  }

  const knowMoreLink = knowMoreUrl || LOYALTY_KNOW_MORE_URL;
  const loginLink = resolveAuthHref({ route: AUTH_ROUTE_PATHS.login });

  const anonymousRewardText = interpolateCopy(loyaltyRewardCopy?.anonymousRewardText ?? "", { earnPoints: points });

  const noDiscountRewardText = interpolateCopy(loyaltyRewardCopy?.loggedInNoDiscountRewardText ?? "", {
    earnPoints: points,
  });

  const partialDiscountRewardText = interpolateCopy(loyaltyRewardCopy?.loggedInPartialRewardText ?? "", {
    discount: highestTier ? formatDiscountAmount(highestTier.price) : "",
    discountAmount: highestTier?.price ?? "",
    discountPoints: highestTier?.points ?? "",
    earnPoints: points,
  });

  const rewardCtaHref = isRewardCopyAuthoringEnabled
    ? loyaltyDiscountState === "anonymous"
      ? loginLink
      : knowMoreLink
    : knowMoreLink;
  const rewardCtaText = isRewardCopyAuthoringEnabled
    ? loyaltyDiscountState === "anonymous"
      ? (loyaltyRewardCopy?.anonymousCtaText ?? "")
      : loyaltyDiscountState === "logged-in-partial-discount"
        ? (loyaltyRewardCopy?.loggedInPartialRewardCtaText ?? "")
        : (loyaltyRewardCopy?.loggedInNoDiscountCtaText ?? "")
    : translate("product.info.knowMore");
  const shouldRenderAnonymousRewardText =
    !isTimeLimited &&
    isRewardCopyAuthoringEnabled &&
    loyaltyDiscountState === "anonymous" &&
    Boolean(anonymousRewardText);
  const shouldRenderNoDiscountRewardText =
    !isTimeLimited &&
    isRewardCopyAuthoringEnabled &&
    loyaltyDiscountState === "logged-in-no-discount" &&
    Boolean(noDiscountRewardText);
  const shouldRenderPartialRewardText =
    !isTimeLimited &&
    isRewardCopyAuthoringEnabled &&
    loyaltyDiscountState === "logged-in-partial-discount" &&
    Boolean(partialDiscountRewardText);
  const shouldRenderRewardCta = !isTimeLimited && Boolean(rewardCtaText);

  return (
    <div className={className}>
      <div className={styles.container}>
        <Box title={translate("product.info.reward")}>
          {!isLoaded && <DEPRECATED_Loading />}
          {isTimeLimited && (
            <span className="loyalty-reward-block__circle ">
              <span>{translate("product.info.multiplier")}</span>
            </span>
          )}
          {isTimeLimited && (
            <h3 className="productDetails__rowContainer-title">
              <span className="subtitle">{translate("product.info.limitedOffer")}</span>
            </h3>
          )}
          <div className="loyalty-reward-block__loyaltyPointsLoading">
            <div className="loyalty-reward-block__reward">
              <div className="loyalty-reward-block__reward-logo-container">
                <LoyaltyLogo />
              </div>
              <div className="loyalty-reward-block__reward-content" data-testid="product-points">
                {isTimeLimited && (
                  <div className="loyalty-reward-block__timer-container">
                    <Badge
                      label={setLimitedRewardTimerContent(
                        timeLimited,
                        hours,
                        minutes,
                        dayssingular,
                        daysplural,
                        weekssingular,
                        weeksplural
                      )}
                      icon={TimeIcon}
                    />
                  </div>
                )}
                {!isTimeLimited && !isRewardCopyAuthoringEnabled && (
                  <span
                    className="productDetails__stockText loyalty-reward-block__reward"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(translate("product.info.collectPoints", [points])),
                    }}
                  />
                )}
                {shouldRenderAnonymousRewardText && <RewardText>{anonymousRewardText}</RewardText>}
                {shouldRenderNoDiscountRewardText && <RewardText>{noDiscountRewardText}</RewardText>}
                {shouldRenderPartialRewardText && <RewardText>{partialDiscountRewardText}</RewardText>}
                {isTimeLimited && (
                  <span
                    className="productDetails__stockText loyalty-reward-block--limitedReward"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(translate("product.info.collectLimitedPoints", [points])),
                    }}
                  />
                )}
                {shouldRenderRewardCta && (
                  <a href={rewardCtaHref} className="productDetails__stockText productDetails__stockText--link">
                    {rewardCtaText}
                  </a>
                )}
              </div>
            </div>
          </div>
        </Box>
      </div>
    </div>
  );
};
